import { Injectable } from '@angular/core';
import { CatalogItem, OccCatalogItem, OccCatalogParams } from '@catalog/const/v2';
import { CatalogActions, CatalogApiService, OccCatalogService } from '@catalog/data-access/v2';
import { CityActions } from '@city/data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, mergeMap, switchMap } from 'rxjs/operators';

function mapNode({ uid, entries, children, hasChildren }: OccCatalogItem): CatalogItem {
  const { linkName, url, categoryCode, media } = entries[0];

  return {
    url,
    media,
    nodeId: uid,
    name: linkName,
    code: categoryCode,
    children: children?.map(mapNode),
    hasChildren,
  };
}

@Injectable()
export class CatalogEffects {
  loadRootNodeId$: Observable<CatalogActions.CatalogRootNodeIdLoadSuccess | CatalogActions.CatalogRootNodeIdLoadFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CatalogActions.LOAD_CATALOG_ROOT_NODE_ID),
        switchMap(() => {
          return this.catalogApiService.getRootNodeId().pipe(
            map((res: string) => {
              return new CatalogActions.CatalogRootNodeIdLoadSuccess(res);
            }),
            catchError((error: unknown) => of(new CatalogActions.CatalogRootNodeIdLoadFail(error))),
          );
        }),
      ),
    );

  loadRootNode$: Observable<CatalogActions.CatalogLoadSuccess | CatalogActions.CatalogLoadFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogActions.LOAD_CATALOG_ROOT_NODE_ID_SUCCESS),
      map((action: CatalogActions.CatalogRootNodeIdLoadSuccess) => action.payload),
      switchMap((rootNodeId: string) => {
        return this.occCatalogService.load({ nodeId: rootNodeId, level: 2 }).pipe(
          map((res: OccCatalogItem) => {
            return new CatalogActions.CatalogLoadSuccess({
              nodeId: rootNodeId,
              level: 2,
              entry: res.entries?.[0],
              items: res.children?.map(mapNode) || [],
            });
          }),
          catchError((error: unknown) => of(new CatalogActions.CatalogLoadFail(error))),
        );
      }),
    ),
  );

  loadCatalog$: Observable<CatalogActions.CatalogLoadSuccess | CatalogActions.CatalogLoadFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogActions.LOAD_CATALOG),
      map((action: CatalogActions.CatalogLoad) => action.payload),
      mergeMap(({ nodeId, level }: OccCatalogParams) => {
        return this.occCatalogService.load({ nodeId, level }).pipe(
          map((res: OccCatalogItem) => {
            return new CatalogActions.CatalogLoadSuccess({
              nodeId,
              level: Number(level),
              entry: res.entries?.[0],
              items: res.children?.map(mapNode) || [],
            });
          }),
          catchError((error: unknown) => of(new CatalogActions.CatalogLoadFail(error))),
        );
      }),
    ),
  );

  clearCatalogStateOnChangeBaseStore$: Observable<CatalogActions.CatalogClearState> = createEffect(() =>
    this.actions$.pipe(
      ofType(CityActions.SET_ACTIVE_BASE_STORE),
      map(({ payload }) => payload),
      distinctUntilChanged(),
      map(() => new CatalogActions.CatalogClearState()),
    ),
  );

  constructor(
    private actions$: Actions,
    private catalogApiService: CatalogApiService,
    private occCatalogService: OccCatalogService,
  ) {}
}
